(() => {
  'use strict';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const disclosures = [...document.querySelectorAll('[data-bexco-disclosure]')];
  function closeMenus(except) { disclosures.forEach(el => { if (el !== except) el.open = false; }); }
  disclosures.forEach(el => el.addEventListener('toggle', () => { if (el.open) closeMenus(el); }));
  document.addEventListener('click', e => { if (!e.target.closest('[data-bexco-disclosure]')) closeMenus(); });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      const opened = disclosures.find(el => el.open);
      if (opened) { opened.open = false; opened.querySelector('summary')?.focus(); }
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !e.altKey) {
      const search = document.querySelector('.header__search summary');
      if (search && !document.querySelector('dialog[open]')) { e.preventDefault(); closeMenus(); search.click(); }
    }
  });
  document.addEventListener('focusin', e => { if (!e.target.closest('.header')) closeMenus(); });
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;
  const productSelector = 'product-info[id^="MainProduct-"]';
  const barSelector = '[data-bexco-purchase-bar]';
  const unloading = new WeakSet();
  let purchase = null, preferredOwner = null, pendingFrame = null;

  const sourcesFor = (bar) => {
    const owner = bar.closest(productSelector);
    if (!owner || !owner.isConnected || unloading.has(owner)) return null;
    const find = (prefix) => owner.querySelector('#' + CSS.escape(prefix + bar.dataset.section));
    const section = find('ProductInfo-'), submit = find('ProductSubmitButton-'), price = find('price-');
    const label = bar.querySelector('[data-bexco-live-price]'), button = bar.querySelector('[data-bexco-return-to-options]');
    if (!section || !submit || !price || !label || !button) return null;
    return {bar, owner, section, submit, price, label, button};
  };
  const sourcesMatch = (state) => state.bar.isConnected && state.owner.isConnected &&
    !unloading.has(state.owner) && ['section', 'submit', 'price'].every((name) => {
      const prefix = {section:'ProductInfo-', submit:'ProductSubmitButton-', price:'price-'}[name];
      return state[name].isConnected && state.owner.querySelector('#' + CSS.escape(prefix + state.bar.dataset.section)) === state[name];
    });
  const schedulePurchase = () => {
    if (pendingFrame !== null) return;
    pendingFrame = requestAnimationFrame(() => { pendingFrame = null; initPurchase(); });
  };
  const disposePurchase = (restore = true) => {
    if (!purchase) return;
    const state = purchase;
    purchase = null;
    state.observers.forEach(observer => observer.disconnect());
    state.button.removeEventListener('click', state.onClick);
    state.bar.hidden = true;
    state.label.textContent = '';
    // Restore our own markup for an in-place source replacement, not an unload.
    if (restore && state.owner.isConnected && state.marker.isConnected && !unloading.has(state.owner)) {
      state.marker.replaceWith(state.bar);
    } else {
      state.marker.remove();
      state.bar.remove();
    }
  };
  const bindPurchase = (state) => {
    purchase = state;
    state.marker = document.createComment('bexco-purchase-bar');
    state.bar.before(state.marker);
    state.bar.hidden = true;
    document.body.append(state.bar);
    state.observers = [];
    let originalVisible = true;
    state.updateBar = () => {
      if (purchase !== state) return;
      if (!sourcesMatch(state)) { state.bar.hidden = true; schedulePurchase(); return; }
      const above = state.submit.getBoundingClientRect().bottom < 0;
      state.bar.hidden = originalVisible || !above || state.submit.disabled || state.price.classList.contains('hidden') || !!document.querySelector('dialog[open]');
    };
    const updatePrice = () => {
      if (purchase !== state) return;
      const sale = state.price.querySelector('.price--on-sale .price__sale .price-item--sale');
      const regular = state.price.querySelector('.price__regular .price-item--regular');
      const value = state.price.classList.contains('hidden') ? '' : (sale || regular)?.textContent.trim() || '';
      if (state.label.textContent !== value) state.label.textContent = value;
      state.updateBar();
    };
    const intersection = new IntersectionObserver(entries => {
      if (purchase !== state) return;
      originalVisible = entries[0].isIntersecting;
      state.updateBar();
    }, {threshold: 0});
    intersection.observe(state.submit);
    const priceObserver = new MutationObserver(updatePrice);
    priceObserver.observe(state.price, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['class']});
    const submitObserver = new MutationObserver(state.updateBar);
    submitObserver.observe(state.submit, {attributes:true,attributeFilter:['disabled']});
    state.observers.push(intersection, priceObserver, submitObserver);
    state.onClick = () => {
      if (!sourcesMatch(state)) { disposePurchase(); schedulePurchase(); return; }
      const target = state.section.querySelector('variant-selects') || state.submit;
      target.scrollIntoView({behavior:reducedMotion()?'instant':'smooth',block:'center'});
      const focus = target.querySelector('input:not([disabled]),select:not([disabled])') || state.submit;
      focus.focus({preventScroll:true});
    };
    state.button.addEventListener('click', state.onClick);
    updatePrice();
  };
  const initPurchase = () => {
    if (purchase && !sourcesMatch(purchase)) disposePurchase();
    const candidates = [...document.querySelectorAll(barSelector)]
      .filter(bar => bar !== purchase?.bar).map(sourcesFor).filter(Boolean);
    // Scope every lookup to its product root: Dawn briefly keeps an old and a
    // new product in the DOM during a swap, renaming the old product's IDs.
    const next = candidates.find(state => state.owner === preferredOwner) ||
      (purchase ? candidates.find(state => state.owner === purchase.owner) : candidates[0]);
    preferredOwner = null;
    if (next) { disposePurchase(false); bindPurchase(next); }
    else purchase?.updateBar();
  };
  const productRoots = (target) => target instanceof Element ?
    [...(target.matches(productSelector) ? [target] : []), ...target.querySelectorAll(productSelector)] : [];
  const onProductLoad = (event) => {
    const owners = productRoots(event.target);
    owners.forEach(owner => unloading.delete(owner));
    preferredOwner = owners[0] || preferredOwner;
    schedulePurchase();
  };
  document.addEventListener('product-info:loaded', onProductLoad);
  document.addEventListener('shopify:section:load', onProductLoad);
  document.addEventListener('shopify:section:unload', (event) => {
    const owners = productRoots(event.target);
    owners.forEach(owner => unloading.add(owner));
    if (purchase && owners.includes(purchase.owner)) disposePurchase(false);
    if (owners.includes(preferredOwner)) preferredOwner = null;
    schedulePurchase();
  });
  // One document observer detects detached/replaced sources and dialog state;
  // per-product observers/listeners are disconnected on every disposal.
  new MutationObserver(records => {
    if (records.some(record => record.type === 'childList')) schedulePurchase();
    if (records.some(record => record.type === 'attributes' && record.target.matches('dialog'))) purchase?.updateBar();
  }).observe(document.body, {childList:true,subtree:true,attributes:true,attributeFilter:['open']});
  initPurchase();
})();
